import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Search, X, Navigation } from 'lucide-react';
import { loadGoogleMaps, parseAddressComponents } from '../utils/googleMaps';

const MapPickerModal = ({ onClose, onConfirm, initialLat, initialLng }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const geocoderRef = useRef(null);
  const [displayAddress, setDisplayAddress] = useState('Move the map to select location...');
  const [addressData, setAddressData] = useState(null);
  const [mapsLoading, setMapsLoading] = useState(true);
  const [mapsError, setMapsError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadGoogleMaps()
      .then(() => {
        const center = {
          lat: initialLat || 28.4035949,
          lng: initialLng || 76.9733813,
        };

        const map = new window.google.maps.Map(mapRef.current, {
          center,
          zoom: 16,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'greedy',
        });

        mapInstanceRef.current = map;
        geocoderRef.current = new window.google.maps.Geocoder();

        reverseGeocode(center);

        // Fire reverse geocode each time the map stops moving
        map.addListener('idle', () => {
          const c = map.getCenter();
          reverseGeocode({ lat: c.lat(), lng: c.lng() });
        });

        setMapsLoading(false);
      })
      .catch((err) => {
        setMapsError(err.message || 'Could not load Google Maps.');
        setMapsLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reverseGeocode = (latlng) => {
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ location: latlng }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const parsed = parseAddressComponents(results[0].address_components);
        setDisplayAddress(results[0].formatted_address);
        setAddressData({
          address: results[0].formatted_address,
          city: parsed.city,
          state: parsed.state,
          pincode: parsed.pincode,
          country: parsed.country,
          latitude: latlng.lat,
          longitude: latlng.lng,
        });
      }
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || !window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: searchQuery }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        mapInstanceRef.current.setCenter(loc);
        mapInstanceRef.current.setZoom(16);
      }
    });
  };

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latlng = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        mapInstanceRef.current?.setCenter(latlng);
        mapInstanceRef.current?.setZoom(17);
      },
      () => {}
    );
  };

  return (
    <div className="fixed inset-0 z-[200] flex flex-col">
      {/* Top search bar */}
      <div className="absolute top-0 left-0 right-0 z-10 p-3 pt-safe">
        <div className="flex gap-2 items-center">
          <button
            onClick={onClose}
            className="bg-white rounded-full p-2.5 shadow-lg flex-shrink-0"
          >
            <X size={20} className="text-gray-700" />
          </button>
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="flex-1 flex items-center bg-white rounded-full px-4 py-2.5 shadow-lg gap-2">
              <Search size={16} className="text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for a place..."
                className="flex-1 outline-none text-sm bg-transparent"
              />
            </div>
            <button
              type="submit"
              className="bg-blue-600 text-white rounded-full px-4 py-2.5 shadow-lg text-sm font-semibold"
            >
              Go
            </button>
          </form>
        </div>
      </div>

      {/* Map or error state */}
      {mapsError ? (
        <div className="flex-1 flex items-center justify-center bg-gray-100">
          <div className="text-center p-6 max-w-xs">
            <MapPin size={48} className="text-gray-400 mx-auto mb-3" />
            <p className="text-gray-700 font-medium mb-1">Map unavailable</p>
            <p className="text-gray-500 text-sm">{mapsError}</p>
            <button
              onClick={onClose}
              className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-xl text-sm font-semibold"
            >
              Go Back
            </button>
          </div>
        </div>
      ) : (
        <div ref={mapRef} className="w-full h-full" />
      )}

      {/* Fixed center pin */}
      {!mapsError && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center" style={{ paddingBottom: '160px' }}>
          <div className="flex flex-col items-center drop-shadow-xl">
            <div className="bg-blue-600 rounded-full p-2 shadow-xl">
              <MapPin size={28} className="text-white" fill="white" />
            </div>
            <div className="w-0.5 h-4 bg-blue-600" />
            <div className="w-4 h-1 bg-blue-900/20 rounded-full blur-sm" />
          </div>
        </div>
      )}

      {/* Current location button */}
      {!mapsError && (
        <button
          onClick={handleCurrentLocation}
          className="absolute right-4 z-10 bg-white rounded-full p-3 shadow-lg"
          style={{ bottom: '180px' }}
        >
          <Navigation size={20} className="text-blue-600" />
        </button>
      )}

      {/* Bottom address panel */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-10 p-5">
        <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
          Selected Address
        </p>
        <div className="flex items-start gap-2 mb-4 min-h-[40px]">
          <MapPin size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-gray-800 text-sm leading-snug">
            {mapsLoading ? 'Loading map…' : displayAddress}
          </p>
        </div>
        <button
          onClick={() => addressData && onConfirm(addressData)}
          disabled={!addressData || mapsLoading}
          className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold text-base disabled:bg-gray-200 disabled:text-gray-400 transition-all"
        >
          Use This Location
        </button>
      </div>
    </div>
  );
};

export default MapPickerModal;
